"use client";
import { VariantProps, cva } from "class-variance-authority";
import React from "react";
import { twMerge, twJoin } from "tailwind-merge";

export const buttonStyles = cva("text-base text-gray-500 ", {
  variants: {
    size: {
      xs: "px-2 text-xs",
      sm: "px-2 text-sm",
      md: "px-3 text-base",
      lg: "px-5 text-lg",
      xl: "text-xl",
    },
    weight: {
      normal: "font-normal",
      semiBold: "font-semibold",
    },
    variant: {
      default: "bg-[#dadbdd] hover:bg-gray-300 text-gray-700",
      primary: "bg-[#dadbdd]  hover:bg-gray-300 text-[#4394e5]",
      destructive: "bg-[#dadbdd] hover:bg-gray-300 text-red-500",
      black: "bg-[#dadbdd]  hover:bg-gray-300 text-gray-700",
      outline:
        "bg-white border-2 border-gray-300 border-solid text-gray-700 hover:bg-gray-100",
    },
    disabled: {
      true: "relative cursor-not-allowed opacity-70",
    },
    rounded: {
      true: "rounded-xl",
    },
    filled: {
      false: null,
      true: [""],
    },
    compact: {
      true: "px-2 text-sm",
    },
    elevation: {
      1: "shadow-sm",
      2: "shadow-md",
    },
    text: {
      true: "bg-transparent hover:bg-transparent hover:underline",
    },

    alignment: {
      left: "justify-start",
      center: "justify-center",
      right: "justify-end",
    },
    justifyContent: {
      center: "justify-center",
    },
  },
  compoundVariants: [
    {
      variant: "primary",
      filled: true,
      className: "bg-[#4394e5] text-white hover:bg-[#357abd]",
    },
    {
      variant: "outline",
      filled: false,
      className: "bg-transparent hover:bg-gray-100",
    },
    {
      variant: "primary",
      disabled: true,
      className: "bg-[#d2d6d8] text-[#262625] opacity-60 ",
    },
  ],
  defaultVariants: {
    variant: "default",
    size: "md",
    rounded: true,
    weight: "semiBold",
    alignment: "center",
  },
});

export const buttonSizeStyles = cva("flex items-center", {
  variants: {
    size: {
      xs: "h-7",
      sm: "h-8",
      md: "h-9",
      lg: "h-11",
      xl: "h-16",
    },
  },

  defaultVariants: {
    size: "md",
  },
});

export type ButtonStyleProps = VariantProps<typeof buttonStyles>;

export type ButtonProps = ButtonStyleProps &
  React.DetailedHTMLProps<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
  > & {
    isLoading?: boolean;
    leftIcon?: React.ReactNode;
    fullWidth?: boolean;
    compact?: boolean;
  };

export const ButtonV2 = React.forwardRef<HTMLButtonElement, ButtonProps>(
  function _Button(
    {
      variant = "default",
      fullWidth = false,
      leftIcon,
      disabled,
      isLoading,
      size,
      children,
      compact,
      filled,
      elevation,
      text,
      alignment = "center",
      justifyContent,
      type = "button",
      ...props
    },
    ref,
  ) {
    return (
      <button
        ref={ref}
        className={twMerge(
          buttonStyles({
            size,
            disabled: disabled || isLoading,
            filled,
            variant,
            compact,
            elevation,
            text,
            alignment,
            justifyContent,
          }),
          buttonSizeStyles({ size }),
          fullWidth && "w-full",
          props.className,
        )}
        disabled={isLoading || !!disabled}
        type={type}
        {...props}
      >
        <div className="flex">
          {leftIcon && (
            <span className={twJoin("mr-1.5", isLoading && "opacity-0")}>
              {leftIcon}
            </span>
          )}
          {children}
        </div>
      </button>
    );
  },
);
